import { useCallback, useMemo } from "react";
import { DefaultValues } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";

import { useCustomerServiceApi } from "@/api/hooks/customerService.hook";
import { handleError } from "@/api/hooks/utils";
import {
    ContactCustomerserviceFormValues,
    categoriesToFormOptions,
    getCategoryByCode,
    getInitialFormValues,
    getPhone
} from "@/components/ContactCustomerservice/helpers/formValues.helpers";
import * as CustomerSelectors from "@/redux/modules/api/data/customer/customer.selectors";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import {
    Translate,
    useGranite
} from "@/redux/modules/value/translations/translations.provider";
import * as ContactStepTracker from "@/services/tracking/trackers/tools/contact/contactStep.tracker";

import ContactCustomerservice from "./contactCustomerservice.component";
import {
    Category,
    ContactCustomerServiceRequest
} from "./contactCustomerservice.types";

const broadcastSuccessMessage = (t: Translate) => () =>
    broadcastMessage({
        id: "contact-customerservice-request-success",
        text: t("contact.customerservice.request.success.message"),
        status: "success",
        dismissible: true,
        activeScroll: true
    });

const broadcastFailureMessage = (t: Translate, message?: string) =>
    broadcastMessage({
        id: "contact-customerservice-request-failure",
        text: message || t("contact.customerservice.request.failure.message"),
        status: "danger",
        dismissible: true,
        activeScroll: true
    });

const broadcastToManyRequestsMessage = (t: Translate) =>
    broadcastMessage({
        id: "contact-customerservice-to-many-failure",
        text: t("contact.customerservice.request.to.many.message"),
        status: "danger",
        dismissible: true,
        activeScroll: true
    });

type ContactCustomerserviceContainerProps = {
    categories: Array<Category>;
};

const ContactCustomerserviceContainer = ({
    categories
}: ContactCustomerserviceContainerProps) => {
    const isLoadingCustomer = useSelector(CustomerSelectors.isLoading);
    const isLoadingPhonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.isLoading
    );
    const customer = useSelector(CustomerSelectors.getCustomer, shallowEqual);
    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );

    const { t } = useGranite();

    const [{ loading }, contactWeb2Case] = useCustomerServiceApi<FormData>(
        {
            url: "/contactform",
            method: "POST",
            headers: { "Content-Type": "multipart/form-data" }
        },
        { manual: true, useCache: false }
    );

    const categoriesFormOptions = useMemo(
        () => categoriesToFormOptions(categories),
        [categories]
    );

    const initialFormValues = useMemo(() => {
        return getInitialFormValues(customer, categories);
    }, [customer, categories]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const handleSubmit = useCallback(
        async (
            {
                email,
                storeId,
                message,
                phone,
                phonePrefix,
                firstName,
                lastName,
                category,
                bonusCardNumber,
                orderId,
                attachments
            }: ContactCustomerserviceFormValues,
            reset: (
                newData?: DefaultValues<ContactCustomerserviceFormValues>
            ) => void
        ): Promise<void> => {
            clearMessageById("contact-customerservice-request-success");
            clearMessageById("contact-customerservice-request-failure");
            clearMessageById("contact-customerservice-to-many-failure");

            const request: ContactCustomerServiceRequest = {
                firstName: firstName || undefined,
                lastName: lastName,
                message: message,
                storeId: storeId,
                orderId: orderId || undefined,
                category: category,
                bonusCardNumber: bonusCardNumber || undefined,
                email: email,
                phone: getPhone(phonenumberPrefixes, phonePrefix, phone)
            };

            const formData = new FormData();
            formData.append(
                "data",
                new Blob([JSON.stringify(request)], {
                    type: "application/json"
                })
            );

            attachments?.forEach((attachment) => {
                formData.append(
                    "attachments",
                    attachment.fileBody,
                    attachment.fileName
                );
            });

            return contactWeb2Case({ data: formData })
                .then(broadcastSuccessMessage(t))
                .then(() => {
                    ContactStepTracker.trackSubmit({
                        storeId: request.storeId,
                        email: request.email,
                        rubriek: getCategoryByCode(categories)(request.category)
                    });
                    reset({
                        ...getInitialFormValues(customer, categories),
                        category: initialFormValues.category
                    });
                })
                .catch((e) => {
                    if (e.status === 429) {
                        broadcastToManyRequestsMessage(t);
                    } else {
                        const { message } = handleError(e);
                        broadcastFailureMessage(t, message);
                    }
                });
        },
        [phonenumberPrefixes, categories, t]
    );

    return (
        <ContactCustomerservice
            onSubmit={handleSubmit}
            isSubmitting={loading}
            initialFormValues={initialFormValues}
            categoriesFormOptions={categoriesFormOptions}
            loading={isLoadingCustomer || isLoadingPhonenumberPrefixes}
        />
    );
};

export { ContactCustomerserviceContainer };
